import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import MuiLink from '@mui/material/Link';
import { useTheme } from '@mui/material/styles';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { RepairJobAction } from './RepairJobAction';
import { ActionPermissions, CustomPermission } from '../../../@types';
import { PartLocationType } from '../../../@types/part.type';
import { PrintLabelEntityTypes } from '../../../@types/print-label.type';
import { isUserHasPermission } from '../../../configs/permissions';
import { formatFriendlyDateTime } from '../../../utils/common';
import { getPartLocationLinkByLocationType } from '../../../utils/part';
import { NotesCell } from '../../atoms/NotesCell';
import { PrintLabel } from '../../molecules/PintLabel';
import { TagCell } from '../../molecules/TagCell';
import { TagCellItem } from '../../molecules/TagCell/TagCell.props';
import { ColumnDef } from '../ServiceTable';

// Part-row fields that hold ISO timestamps and should render as
// "8 May 2026, 1:13 AM" instead of the raw ISO string.
const DATE_FIELD_KEYS: ReadonlySet<string> = new Set(['createdAt', 'updatedAt', 'warrantyEnd']);

const NOTES_FIELD_KEYS: ReadonlySet<string> = new Set([
  'creationNotes',
  'notes',
  'exceptionResultNote',
]);

export interface PartTableRowProps {
  row: Record<string, unknown>;
  columns: ColumnDef[];
  selected: boolean;
  even: boolean;
  onClick: () => void;
  onShiftClick: () => void;
  permissions?: CustomPermission[];
  /** When true, the selection-checkbox cell and the trailing action cell
   *  (Create Repair Job) are dropped. Used for client/customer users — see
   *  PartTable for the surface-level rationale. */
  isClientCustomer?: boolean;
}

const CELL_SX = {
  fontSize: '11px',
  py: 0.25,
  px: 1,
  borderBottom: '1px solid',
  borderColor: 'v4.border',
} as const;

const INLINE_LINK_SX = {
  color: 'v4.accent',
  textDecoration: 'none',
  '&:hover': { textDecoration: 'underline' },
} as const;

const YES_NO = (v: unknown) => (v ? 'Yes' : 'No');

const stopRowClick = (e: React.MouseEvent) => e.stopPropagation();

// `serialised` arrives from the `partlist` view as the string 'Serialised' or
// 'Non-Serialised' (the view's CASE yields NULL for any other part-type
// category), and as a boolean or stringified boolean from the older payload
// shapes. Anything else — undefined, NULL, an unrecognised string — is
// "don't know": the wrench keeps showing so we don't accidentally hide it for
// serialised parts, and Qty stays blank rather than inventing a quantity.
const SERIALISED_VALUES: ReadonlySet<unknown> = new Set([true, 'true', 'Serialised']);
const CONSUMABLE_VALUES: ReadonlySet<unknown> = new Set([false, 'false', 'Non-Serialised']);

const isSerialisedRow = (row: Record<string, unknown>): boolean =>
  SERIALISED_VALUES.has(row.serialised);

const isConsumableRow = (row: Record<string, unknown>): boolean =>
  CONSUMABLE_VALUES.has(row.serialised);

const parseTags = (raw: unknown): TagCellItem[] | null => {
  if (typeof raw !== 'string' || !raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as TagCellItem[]) : null;
  } catch {
    return null;
  }
};

/** Redundant prefix the BFF prepends to a parent-part location segment. */
const PART_LABEL_PREFIX = 'Part ';

const renderLocationBreadcrumb = (row: Record<string, unknown>) => {
  const locationData = row.location as string | undefined;
  if (!locationData || locationData === 'Amtek') return locationData ?? '';

  const locations = locationData.split(' / ');
  const locationIds = (row.locationIds as string | undefined)?.split(' / ') ?? [];
  const locationNodeTypes = (row.locationNodeTypes as string | undefined)?.split(' / ') ?? [];

  return (
    <Box component="span">
      {locations.map((loc, index) => {
        const nodeType = locationNodeTypes[index] ?? '';
        // A part nested inside another part arrives from the BFF with a
        // redundant "Part " prefix on that parent-part segment (e.g.
        // "Part 100234"). Strip it so the breadcrumb shows just the serial,
        // consistent with how structure-node segments render. Guarded on the
        // node type so a real node literally named "Part …" is left untouched.
        const label =
          nodeType === PartLocationType.Part && loc.startsWith(PART_LABEL_PREFIX)
            ? loc.slice(PART_LABEL_PREFIX.length)
            : loc;
        const href = getPartLocationLinkByLocationType(nodeType, locationIds[index] ?? '');
        return (
          <React.Fragment key={`${loc}-${index}`}>
            <MuiLink component={RouterLink} to={href} onClick={stopRowClick} sx={INLINE_LINK_SX}>
              {label}
            </MuiLink>
            {index !== locations.length - 1 && ' / '}
          </React.Fragment>
        );
      })}
    </Box>
  );
};

export const PartTableRow: React.FC<PartTableRowProps> = React.memo(
  ({
    row,
    columns,
    selected,
    even,
    onClick,
    onShiftClick,
    permissions,
    isClientCustomer = false,
  }) => {
    const theme = useTheme();
    const id = row.id as number | undefined;
    const assetId = row.assetId as number | undefined;
    const serial1 = row.serial1 as string | undefined;
    // Permission decides whether the action exists at all; being a consumable
    // only disables it, so the column stays aligned and the reason is visible
    // on hover rather than the control silently vanishing.
    const canCreateRepairJob =
      !isClientCustomer && isUserHasPermission(ActionPermissions.Service_Create, permissions);
    const repairJobDisabled = isConsumableRow(row);

    const handleRowClick = (e: React.MouseEvent) => {
      if (isClientCustomer) return;
      if (e.shiftKey) onShiftClick();
      else onClick();
    };

    const getRowBg = () => {
      if (selected) return theme.palette.v4.tableSelected;
      if (even) return theme.palette.v4.tableEven;
      return 'background.paper';
    };

    const getHoverBg = () =>
      selected ? theme.palette.v4.tableSelectedHover : theme.palette.v4.tableHover;

    const renderCell = (col: ColumnDef) => {
      const value = row[col.key];

      if (col.key === 'assetId' && assetId != null) {
        if (!id) return String(assetId);
        return (
          <MuiLink
            component={RouterLink}
            to={`/parts/${id}`}
            onClick={stopRowClick}
            sx={{ color: theme.palette.v4.accent, fontWeight: 600, textDecoration: 'none' }}
          >
            {String(assetId)}
          </MuiLink>
        );
      }

      if (col.key === 'serial1' && id) {
        return (
          <MuiLink
            component={RouterLink}
            to={`/parts/${id}`}
            onClick={stopRowClick}
            sx={INLINE_LINK_SX}
          >
            {value != null ? String(value) : ''}
          </MuiLink>
        );
      }

      if (col.key === 'conditionName') {
        const name = (row.partCondition as { name?: string } | undefined)?.name;
        return name ? name.replace(/-/g, ' ') : value != null ? String(value) : '';
      }

      if (col.key === 'tags' || col.key === 'partTypeTags') {
        const tags = parseTags(value);
        return tags ? <TagCell tags={tags} /> : '';
      }

      if (col.key === 'location') {
        return renderLocationBreadcrumb(row);
      }

      if (col.key === 'exceptionServiceId' && value != null) {
        return isUserHasPermission(ActionPermissions.Service_Edit_View_Service, permissions) ? (
          <MuiLink
            component={RouterLink}
            to={`/services/${value}`}
            onClick={stopRowClick}
            sx={INLINE_LINK_SX}
          >
            {String(value)}
          </MuiLink>
        ) : (
          String(value)
        );
      }

      if (col.key === 'printLabel') {
        if (!assetId) return '';
        return <PrintLabel entityId={assetId} entityType={PrintLabelEntityTypes.Part} />;
      }

      if (col.key === 'quantityInStock') {
        if (value != null && value !== '') return String(value);
        // A serialised part has no stock quantity — the `partlist` view selects
        // NULL for it, since one serial is one unit. Read that as 1 so the grid
        // agrees with the CSV/Excel export, which already maps it that way.
        return isSerialisedRow(row) ? '1' : '';
      }

      if (col.key === 'partTypeIsActive' || col.key === 'isActive') {
        return YES_NO(value);
      }

      if (col.key === 'serialised') {
        if (value === true || value === 'true') return 'Serialised';
        if (value === false || value === 'false') return 'Consumable';
        if (typeof value === 'string') return value;
        return '';
      }

      if (NOTES_FIELD_KEYS.has(col.key)) {
        return <NotesCell value={value} maxWidth={col.maxWidth ?? 200} />;
      }

      if (DATE_FIELD_KEYS.has(col.key)) {
        return formatFriendlyDateTime(
          typeof value === 'string' || value instanceof Date ? value : null
        );
      }

      return value != null ? String(value) : '';
    };

    return (
      <TableRow
        data-testid="part-table-row"
        onClick={handleRowClick}
        sx={{
          bgcolor: getRowBg(),
          cursor: isClientCustomer ? 'default' : 'pointer',
          '&:hover': { bgcolor: getHoverBg() },
          transition: 'background-color 0.15s ease',
        }}
      >
        {!isClientCustomer && (
          <TableCell padding="checkbox" sx={{ ...CELL_SX, width: 48, minWidth: 48, maxWidth: 48 }}>
            <Checkbox
              size="small"
              checked={selected}
              onClick={stopRowClick}
              onChange={() => onClick()}
              slotProps={{ input: { 'aria-label': `Select part ${assetId ?? id ?? ''}` } }}
            />
          </TableCell>
        )}
        {columns.map((col) => (
          <TableCell
            key={col.key}
            sx={{
              ...CELL_SX,
              ...(col.width !== undefined
                ? {
                    width: col.width,
                    minWidth: col.width,
                    maxWidth: col.width,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }
                : {
                    ...(col.minWidth !== undefined ? { minWidth: col.minWidth } : {}),
                    ...(col.maxWidth !== undefined
                      ? { maxWidth: col.maxWidth, overflow: 'hidden' }
                      : {}),
                  }),
            }}
          >
            {renderCell(col)}
          </TableCell>
        ))}
        {!isClientCustomer && (
          <TableCell
            sx={{
              ...CELL_SX,
              width: 70,
              position: 'sticky',
              right: 0,
              bgcolor: 'inherit',
              zIndex: 1,
            }}
          >
            {canCreateRepairJob && (
              <RepairJobAction
                serial1={serial1}
                label={`Create repair job from part ${assetId ?? id ?? ''}`}
                disabled={repairJobDisabled}
                onClick={stopRowClick}
              />
            )}
          </TableCell>
        )}
      </TableRow>
    );
  }
);

PartTableRow.displayName = 'PartTableRow';
